import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart, Target, TrendingUp, CheckCircle, ArrowLeft, Database, Eye, Activity } from 'lucide-react';

const BrokerAnalytics = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Breadcrumb */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 text-gray-400">
            <Link to="/solutions" className="hover:text-bluemp-400 transition-colors">Solutions</Link>
            <span>/</span>
            <span className="text-white">Broker Firm AI Analytics</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors mb-6">
                <ArrowLeft size={16} />
                Back to Solutions
              </Link>
              
              <h1 className="display-large text-white mb-6">
                Empowering Broker Firms with a{' '}
                <span className="text-bluemp-400">360° View</span>
              </h1>
              
              <p className="body-large text-gray-300 mb-8">
                Unified client, portfolio, and AUM intelligence that consolidates insurer and investment data for smarter business decisions. Transform fragmented data into actionable insights for your entire organization.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Unify Your Analytics
                  <ArrowRight size={20} />
                </Link>
                <Link to="/experience" className="btn-secondary">
                  See Success Stories
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <BarChart size={64} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4">Unified Intelligence Platform</h3>
                <p className="body-medium text-gray-300 mb-6">
                  Consolidate all client and portfolio data into a single, intelligent platform that provides unprecedented visibility into your business.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">360° client visibility</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Real-time dashboards</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Performance tracking</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Core Capabilities
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Transform fragmented data sources into unified intelligence that drives better business decisions and improved client relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Database,
                title: "Data Consolidation & Unification",
                description: "Seamlessly integrate data from multiple insurers, investment platforms, and internal systems into a unified view."
              },
              {
                icon: Eye,
                title: "Portfolio & AUM Analysis", 
                description: "Comprehensive analysis of client portfolios and assets under management with intelligent insights and trends."
              },
              {
                icon: TrendingUp,
                title: "Adviser Performance Tracking",
                description: "Monitor and analyze adviser performance metrics, client satisfaction, and business development progress."
              },
              {
                icon: Activity,
                title: "Real-time Dashboards & Reporting",
                description: "Dynamic dashboards and automated reporting that provide instant visibility into key business metrics."
              }
            ].map((capability, index) => (
              <div key={index} className="group dark-hover bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <capability.icon size={48} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {capability.title}
                </h3>
                <p className="body-medium text-gray-300">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Transform Your Broker Analytics?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Gain unprecedented visibility into your business with unified analytics that drive better decisions and improved client outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Get Unified Analytics
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions/campaign-solutions" className="btn-secondary">
              Explore Campaign Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrokerAnalytics;